"use client";

import { useState, useRef, useEffect } from "react";
import { useLocale, useT } from "@/lib/locale-context";
import type { Locale } from "@/lib/i18n";
import { ChevronDown, Check, Globe } from "lucide-react";

// A globe and the language code, never a flag emoji (owner, 2026-10-17;
// Windows draws those as two bare letters anyway).
const locales: { code: Locale; label: string }[] = [
  { code: "az", label: "Az\u0259rbaycan" },
  { code: "ru", label: "\u0420\u0443\u0441\u0441\u043A\u0438\u0439" },
  { code: "en", label: "English" },
];

export default function LanguageToggle() {
  const { locale, setLocale, prefetchLocales } = useLocale();
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // The other locales' strings for this page, fetched while the pointer
  // heads for the menu, so a pick swaps the text in the same frame.
  useEffect(() => {
    if (open) prefetchLocales();
  }, [open, prefetchLocales]);

  const current = locales.find((l) => l.code === locale) || locales[0];

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      {/* Trigger button - a globe, the current language code, a chevron */}
      <button
        onClick={() => setOpen(!open)}
        onPointerEnter={prefetchLocales}
        aria-label={`${t("auth.menu.language")}: ${current.label}`}
        aria-expanded={open}
        // h-8 matches the sign-in button exactly.
        className="flex h-8 items-center gap-1 px-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors text-sm"
      >
        <Globe className="h-4 w-4 text-gray-500" aria-hidden />
        <span className="text-xs font-semibold text-gray-700">{current.code.toUpperCase()}</span>
        <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-44 bg-white rounded-xl border border-gray-200 shadow-lg py-1.5 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
          {locales.map((l) => {
            const isActive = locale === l.code;
            return (
              <button
                key={l.code}
                onClick={() => {
                  setLocale(l.code);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "bg-gray-100 font-semibold text-gray-900"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <span className="font-medium flex-1 text-left">{l.label}</span>
                {isActive && <Check className="w-4 h-4 text-gray-900" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
